import { useState, useCallback, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { DatePickerProps, ViewMode, DateRange } from '@/shared/types/datepicker';
import { DatePickerState } from '../types';
import { InputRef } from '@/shared/types/input';
import { resolveDatePickerTimeConfig } from '../utils/resolveTimeConfig';
import { CLOSE_ALL_DATEPICKERS_EVENT } from '@/shared/utils/modalStack';
import {
  computeDatePickerPortalPosition,
  DATE_PICKER_PORTAL_WIDTH,
  getEstimatedDatePickerPortalHeight,
  isValidPortalHeightForLayout,
  measureDatePickerPortalSize,
} from '../utils/computePortalPosition';

export const useDatePicker = (props: DatePickerProps) => {
  const {
    mode = 'single',
    selectedDate: externalSelectedDate = null,
    selectedRange: externalSelectedRange = { start: null, end: null },
    initialViewMode = 'days',
    closeOnSelect = true,
    autoCloseRange = true,
    disabled = false,
    calendarsCount = 1,
  } = props;

  const timeConfig = resolveDatePickerTimeConfig(props);
  const timeVisible = timeConfig.visible;

  // Con hora visible la confirmación es siempre explícita (Aceptar/Cancelar).
  const effectiveCloseOnSelect = timeVisible ? false : closeOnSelect;
  const effectiveAutoCloseRange = timeVisible ? false : autoCloseRange;

  const [state, setState] = useState<DatePickerState>({
    isOpen: false,
    currentDate: new Date(),
    viewMode: initialViewMode,
    internalDate: null,
    internalRange: { start: null, end: null },
    hoverDate: null,
    portalPosition: { top: 0, left: 0 },
    pendingDate: null,
    pendingRange: { start: null, end: null },
  });

  // Estado adicional para controlar si el portal está listo para renderizar
  const [isPortalReady, setIsPortalReady] = useState(false);

  const inputRef = useRef<InputRef>(null);
  const portalHostRef = useRef<HTMLDivElement>(null);
  const measuredPortalSizeRef = useRef<{ width: number; height: number } | null>(null);

  // Ref para guardar el rango original cuando se abre el picker (para poder revertir con Cancelar)
  const originalRangeRef = useRef<DateRange | null>(null);
  // Ref para guardar la fecha original cuando se abre el picker (para poder revertir con Cancelar)
  const originalDateRef = useRef<Date | null>(null);

  // Manejo de estado controlado/no-controlado
  // Usamos props directamente para verificar si el componente es controlado
  // porque externalSelectedDate tiene un valor por defecto de null
  const isControlled =
    mode === 'single' ? props.selectedDate !== undefined : props.selectedRange !== undefined;

  const isUncontrolled =
    mode === 'single' ? props.selectedDate === undefined : props.selectedRange === undefined;

  // Validar y procesar selectedDate
  // Si closeOnSelect es false, usar pendingDate para la selección visual en el calendario
  // pero usar la fecha confirmada para el input
  const selectedDateForCalendar =
    mode === 'single'
      ? effectiveCloseOnSelect === false && state.pendingDate !== null
        ? state.pendingDate
        : isUncontrolled
          ? state.internalDate
          : externalSelectedDate
      : null;

  const selectedDateForInput =
    mode === 'single' ? (isUncontrolled ? state.internalDate : externalSelectedDate) : null;

  // Validar y procesar selectedRange
  // Si autoCloseRange es false, usar pendingRange para la selección visual en el calendario
  // pero usar el rango confirmado para el input
  const selectedRangeForCalendar =
    mode === 'range'
      ? effectiveAutoCloseRange === false && (state.pendingRange.start || state.pendingRange.end)
        ? state.pendingRange
        : isUncontrolled
          ? state.internalRange
          : externalSelectedRange
      : { start: null, end: null };

  const selectedRangeForInput =
    mode === 'range'
      ? isUncontrolled
        ? state.internalRange
        : externalSelectedRange
      : { start: null, end: null };

  // Función auxiliar para validar fechas
  const isValidDate = (date: unknown): date is Date => {
    return date !== null && date !== undefined && date instanceof Date && !isNaN(date.getTime());
  };

  /**
   * Normaliza una fecha a medianoche en hora local usando el día civil UTC.
   * Corrige el desfase cuando las fechas vienen de ISO (ej. 2026-02-28T00:00:00.000Z)
   * y el calendario pinta celdas en hora local (medianoche local).
   */
  const toLocalDateOnly = useCallback((d: Date): Date => {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);
  }, []);

  // Validar que selectedDate sea una fecha válida (para el calendario)
  const validatedSelectedDate = isValidDate(selectedDateForCalendar)
    ? selectedDateForCalendar
    : null;

  // Claves primitivas: `selectedRangeForCalendar` puede ser un objeto nuevo cada render (p. ej. `{ start: null, end: null }` en modo single).
  const calendarRangeStartMs = isValidDate(selectedRangeForCalendar?.start)
    ? selectedRangeForCalendar!.start.getTime()
    : null;
  const calendarRangeEndMs = isValidDate(selectedRangeForCalendar?.end)
    ? selectedRangeForCalendar!.end.getTime()
    : null;

  // Validar que selectedRange tenga fechas válidas (para el calendario).
  // Se normalizan a "solo fecha" en local para evitar desfase de un día con fechas ISO/UTC.
  const validatedSelectedRange = useMemo(
    () => {
      const start = isValidDate(selectedRangeForCalendar?.start)
        ? selectedRangeForCalendar!.start
        : null;
      const end = isValidDate(selectedRangeForCalendar?.end)
        ? selectedRangeForCalendar!.end
        : null;

      if (timeVisible) {
        return { start, end };
      }

      return {
        start: start ? toLocalDateOnly(start) : null,
        end: end ? toLocalDateOnly(end) : null,
      };
    },
    // Objetos `selectedRangeForCalendar` pueden recrearse cada render; deps = timestamps reales.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: ver comentario anterior
    [calendarRangeStartMs, calendarRangeEndMs, toLocalDateOnly, timeVisible]
  );

  // Validar fechas para el input (solo las confirmadas). Misma normalización para evitar desfase.
  const validatedSelectedDateForInput = isValidDate(selectedDateForInput)
    ? selectedDateForInput
    : null;

  const inputRangeStartMs = isValidDate(selectedRangeForInput?.start)
    ? selectedRangeForInput!.start.getTime()
    : null;
  const inputRangeEndMs = isValidDate(selectedRangeForInput?.end)
    ? selectedRangeForInput!.end.getTime()
    : null;

  const validatedSelectedRangeForInput = useMemo(
    () => ({
      start: isValidDate(selectedRangeForInput?.start)
        ? timeVisible
          ? selectedRangeForInput!.start
          : toLocalDateOnly(selectedRangeForInput!.start)
        : null,
      end: isValidDate(selectedRangeForInput?.end)
        ? timeVisible
          ? selectedRangeForInput!.end
          : toLocalDateOnly(selectedRangeForInput!.end)
        : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional: objeto rango inestable; deps = timestamps
    [inputRangeStartMs, inputRangeEndMs, toLocalDateOnly, timeVisible]
  );

  const resolvePortalLayout = useCallback(
    (measured?: { width: number; height: number }) => {
      if (!inputRef.current) {
        return null;
      }

      const inputElement = inputRef.current.getInputElement();
      if (!inputElement) {
        return null;
      }

      const inputRect = inputElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedHeight = getEstimatedDatePickerPortalHeight({
        timeVisible,
        calendarsCount,
        mode,
      });
      const rawHeight =
        measured?.height ?? measuredPortalSizeRef.current?.height ?? estimatedHeight;
      const portalHeight = isValidPortalHeightForLayout(rawHeight, viewportHeight)
        ? rawHeight
        : estimatedHeight;
      const portalWidth =
        measured?.width ??
        measuredPortalSizeRef.current?.width ??
        DATE_PICKER_PORTAL_WIDTH;

      return computeDatePickerPortalPosition({
        inputRect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        portalWidth,
        portalHeight,
      });
    },
    [timeVisible, calendarsCount, mode]
  );

  const updatePortalPosition = useCallback(
    (measured?: { width: number; height: number }) => {
      const layout = resolvePortalLayout(measured);
      if (!layout) {
        return;
      }

      setState((prev) => ({ ...prev, portalPosition: layout }));
      setIsPortalReady(true);
    },
    [resolvePortalLayout]
  );

  const openPicker = useCallback(() => {
    if (disabled) return;

    // Guardar el rango original cuando se abre el picker (para poder revertir con Cancelar)
    if (mode === 'range') {
      originalRangeRef.current = {
        start: validatedSelectedRangeForInput.start
          ? new Date(validatedSelectedRangeForInput.start)
          : null,
        end: validatedSelectedRangeForInput.end
          ? new Date(validatedSelectedRangeForInput.end)
          : null,
      };
      // Inicializar el rango pendiente con el rango confirmado actual
      setState((prev) => ({
        ...prev,
        pendingRange: {
          start: validatedSelectedRangeForInput.start
            ? new Date(validatedSelectedRangeForInput.start)
            : null,
          end: validatedSelectedRangeForInput.end
            ? new Date(validatedSelectedRangeForInput.end)
            : null,
        },
      }));
    }
    // Guardar la fecha original cuando se abre el picker (para poder revertir con Cancelar)
    if (mode === 'single') {
      originalDateRef.current = validatedSelectedDateForInput
        ? new Date(validatedSelectedDateForInput)
        : null;
      // Inicializar la fecha pendiente con la fecha confirmada actual
      setState((prev) => ({
        ...prev,
        pendingDate: validatedSelectedDateForInput ? new Date(validatedSelectedDateForInput) : null,
      }));
    }

    // Primero calcular la posición antes de abrir
    const layout = resolvePortalLayout();
    if (layout) {
      setState((prev) => ({
        ...prev,
        isOpen: true,
        portalPosition: layout,
      }));
      setIsPortalReady(true);
      return;
    }

    // Fallback: abrir sin posición calculada (se actualizará en el useEffect)
    setState((prev) => ({ ...prev, isOpen: true }));
    setIsPortalReady(false);
  }, [disabled, mode, validatedSelectedDateForInput, validatedSelectedRangeForInput, resolvePortalLayout]);

  const closePicker = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    setIsPortalReady(false);
    measuredPortalSizeRef.current = null;
  }, []);

  useEffect(() => {
    if (disabled) {
      closePicker();
    }
  }, [disabled, closePicker]);

  useEffect(() => {
    const handleCloseAllDatePickers = () => {
      closePicker();
    };

    document.addEventListener(CLOSE_ALL_DATEPICKERS_EVENT, handleCloseAllDatePickers);

    return () => {
      document.removeEventListener(CLOSE_ALL_DATEPICKERS_EVENT, handleCloseAllDatePickers);
    };
  }, [closePicker]);

  const restoreOriginalRange = useCallback(() => {
    // Restaurar el rango original cuando se presiona Cancelar
    if (mode === 'range' && originalRangeRef.current) {
      if (!isControlled) {
        // Si no está controlado, restaurar el estado interno
        setState((prev) => ({
          ...prev,
          internalRange: {
            start: originalRangeRef.current!.start
              ? new Date(originalRangeRef.current!.start)
              : null,
            end: originalRangeRef.current!.end ? new Date(originalRangeRef.current!.end) : null,
          },
        }));
      }
      // Si está controlado, no podemos restaurar porque el estado está controlado externamente
      // El componente padre debería manejar la restauración si es necesario
    }
  }, [mode, isControlled]);

  const restoreOriginalDate = useCallback(() => {
    // Restaurar la fecha original cuando se presiona Cancelar
    if (mode === 'single' && originalDateRef.current !== null) {
      if (!isControlled) {
        // Si no está controlado, restaurar el estado interno
        setState((prev) => ({
          ...prev,
          internalDate: originalDateRef.current ? new Date(originalDateRef.current) : null,
        }));
      }
      // Si está controlado, no podemos restaurar porque el estado está controlado externamente
      // El componente padre debería manejar la restauración si es necesario
    }
  }, [mode, isControlled]);

  const setPendingDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, pendingDate: date }));
  }, []);

  const setPendingRange = useCallback((range: DateRange) => {
    setState((prev) => ({ ...prev, pendingRange: range }));
  }, []);

  const setCurrentDate = useCallback((date: Date) => {
    setState((prev) => ({ ...prev, currentDate: date }));
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  // Función de navegación que faltaba
  const navigate = useCallback((direction: 'prev' | 'next') => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);

      switch (prev.viewMode) {
        case 'days':
          newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
          break;
        case 'months':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
          break;
        case 'years':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 12 : -12));
          break;
      }

      return { ...prev, currentDate: newDate };
    });
  }, []);

  const setSelectedDate = useCallback(
    (date: Date | null, confirm: boolean = false) => {
      if (mode === 'single') {
        if (effectiveCloseOnSelect === true || confirm === true) {
          // Si closeOnSelect es true o se confirma explícitamente, actualizar el estado confirmado
          if (isUncontrolled) {
            setState((prev) => ({ ...prev, internalDate: date, pendingDate: null }));
          }
          // Llamar a onDateChange cuando se confirma (ya sea por closeOnSelect o por botón Aceptar)
          if (effectiveCloseOnSelect === true || confirm === true) {
            props.onDateChange?.(date);
          }
          if (effectiveCloseOnSelect === true) {
            closePicker();
          }
        } else {
          // Si closeOnSelect es false y no se confirma, solo actualizar el estado pendiente para la selección visual
          // No actualizar el estado confirmado ni el input hasta que se presione "Aceptar"
          setState((prev) => ({ ...prev, pendingDate: date }));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onDateChange en deps; evitar recrear en cada cambio de props
    [mode, isUncontrolled, props.onDateChange, effectiveCloseOnSelect, closePicker]
  );

  const setSelectedRange = useCallback(
    (range: DateRange, confirm: boolean = false) => {
      if (mode === 'range') {
        if (effectiveAutoCloseRange === true || confirm === true) {
          // Si autoCloseRange es true o se confirma explícitamente, actualizar el estado confirmado
          if (!isControlled) {
            setState((prev) => ({
              ...prev,
              internalRange: range,
              pendingRange: { start: null, end: null },
            }));
          }
          // Llamar a onRangeChange cuando se confirma (ya sea por autoCloseRange o por botón Aceptar)
          if (effectiveAutoCloseRange === true || confirm === true) {
            props.onRangeChange?.(range);
          }
          if (effectiveAutoCloseRange === true && range.start && range.end) {
            closePicker();
          }
        } else {
          // Si autoCloseRange es false y no se confirma, solo actualizar el estado pendiente para la selección visual
          // No actualizar el estado confirmado ni el input hasta que se presione "Aceptar"
          setState((prev) => ({ ...prev, pendingRange: range }));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onRangeChange en deps; evitar recrear en cada cambio de props
    [mode, isControlled, props.onRangeChange, effectiveAutoCloseRange, closePicker]
  );

  const setHoverDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, hoverDate: date }));
  }, []);

  // Función para manejar la selección de mes
  const handleMonthSelect = useCallback((monthIndex: number) => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);
      newDate.setMonth(monthIndex);
      return {
        ...prev,
        currentDate: newDate,
        viewMode: 'days',
      };
    });
  }, []);

  // Función para manejar la selección de año
  const handleYearSelect = useCallback((year: number) => {
    setState((prev) => {
      const newDate = new Date(prev.currentDate);
      newDate.setFullYear(year);
      return {
        ...prev,
        currentDate: newDate,
        viewMode: 'months',
      };
    });
  }, []);

  // Función para ir a la fecha de hoy
  const goToToday = useCallback(() => {
    const today = new Date();
    setState((prev) => ({
      ...prev,
      currentDate: today,
      viewMode: 'days',
    }));
  }, []);

  useLayoutEffect(() => {
    if (!state.isOpen || !isPortalReady) {
      return;
    }

    const portalEl = portalHostRef.current;
    if (!portalEl) {
      return;
    }

    const syncMeasuredPosition = () => {
      const measured = measureDatePickerPortalSize(portalEl);
      if (!measured) {
        return;
      }

      measuredPortalSizeRef.current = measured;
      updatePortalPosition(measured);
    };

    syncMeasuredPosition();

    const resizeObserver = new ResizeObserver(syncMeasuredPosition);
    resizeObserver.observe(portalEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [state.isOpen, isPortalReady, updatePortalPosition]);

  // Efectos para manejar scroll y resize
  useEffect(() => {
    if (!state.isOpen) return;

    const handleScroll = () => updatePortalPosition(measuredPortalSizeRef.current ?? undefined);
    const handleResize = () => updatePortalPosition(measuredPortalSizeRef.current ?? undefined);

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [state.isOpen, updatePortalPosition]);

  useEffect(() => {
    if (state.isOpen) {
      // Usar un pequeño delay para asegurar que el DOM esté listo
      const timer = setTimeout(() => {
        updatePortalPosition();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [state.isOpen, updatePortalPosition]);

  return {
    state,
    selectedDate: validatedSelectedDate,
    selectedRange: validatedSelectedRange,
    selectedDateForInput: validatedSelectedDateForInput,
    selectedRangeForInput: validatedSelectedRangeForInput,
    inputRef,
    portalHostRef,
    isPortalReady,
    timeConfig,
    effectiveAutoCloseRange,
    actions: {
      openPicker,
      closePicker,
      setCurrentDate,
      setViewMode,
      setSelectedDate,
      setSelectedRange,
      setHoverDate,
      updatePortalPosition,
      navigate,
      handleMonthSelect,
      handleYearSelect,
      goToToday,
      restoreOriginalRange,
      restoreOriginalDate,
      setPendingDate,
      setPendingRange,
    },
  };
};
